import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  InputAdornment,
  CircularProgress,
  alpha,
  useTheme,
  Chip,
  Avatar,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Grid,
} from '@mui/material';
import {
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Person as PersonIcon,
  Visibility as VisibilityIcon,
  Close as CloseIcon,
  Email as EmailIcon,
  Code as CodeIcon,
  ContentCopy as ContentCopyIcon,
} from '@mui/icons-material';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import jobseekerapi from '../../../../services/jobseekerApi';

// Types
interface Education {
  degree?: string;
  institution?: string;
  year?: string;
  [key: string]: any;
}

interface Experience {
  company?: string;
  position?: string;
  duration?: string;
  [key: string]: any;
}

interface JobSeeker {
  _id: string;
  job_seeker_code: string;
  first_name: string;
  last_name: string;
  country_code: number;
  email_id: string;
  reference: string;
  resume: string | null;
  privacy_policy_accept: boolean;
  profile_image: string | null;
  skills: string[];
  stripe_customer_id: string | null;
  is_email_verified: boolean;
  is_process_ongoing: boolean;
  total_ongoing_application: number;
  ongoing_application: number;
  is_active: boolean;
  is_user_login: boolean;
  is_deleted: boolean;
  education: Education[];
  experience: Experience[];
  createdAt: string;
  updatedAt: string;
  __v: number;
  [key: string]: any;
}

const PaymentHistory: React.FC = () => {
  const theme = useTheme();

  // State variables
  const [jobSeekers, setJobSeekers] = useState<JobSeeker[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [page, setPage] = useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(10);
  const [totalRows, setTotalRows] = useState<number>(0);
  const [openModal, setOpenModal] = useState<boolean>(false);
  const [selectedJobSeeker, setSelectedJobSeeker] = useState<JobSeeker | null>(null);
  const [copiedId, setCopiedId] = useState<string | null>(null);

  // Fetch job seekers on component mount and when filters/pagination changes
  useEffect(() => {
    fetchJobSeekers();
  }, [page, rowsPerPage, searchQuery]);

  // Function to fetch job seekers
  const fetchJobSeekers = async () => {
    setLoading(true);
    setError(null);

    try {
      // startToken is page number (1-indexed), endToken is number of records per page
      // const startToken = page + 1;
      const startToken = page + 1;
      const endToken = rowsPerPage;

      const response = await jobseekerapi.post('/jobseeker/jobSeekerPaymentHistory ', {
        startToken,
        endToken,
        search: searchQuery,
        jobseeker_id: ''
      });

      if (response?.data?.status) {
        setJobSeekers(response.data.data || []);
        setTotalRows(response.data.record_count || 0);
      } else {
        setError('Failed to fetch job seekers');
      }
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Failed to fetch job seekers');
      console.error('Error fetching job seekers:', err);
    } finally {
      setLoading(false);
    }
  };

  // Handle page change
  const handleChangePage = (_event: React.MouseEvent<HTMLButtonElement> | null, newPage: number) => {
    setPage(newPage);
  };

  // Handle rows per page change
  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Handle search input change
  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value);
    setPage(0);
  };

  // Reset search
  const resetFilters = () => {
    setSearchQuery('');
    setPage(0);
  };

  // Handle copy payment ID
  const handleCopyPaymentId = async (paymentId: string) => {
    try {
      await navigator.clipboard.writeText(paymentId);
      setCopiedId(paymentId);
      setTimeout(() => setCopiedId(null), 2000);
    } catch (err) {
      console.error('Failed to copy payment ID:', err);
    }
  };

  // Handle modal open
  const handleViewDetails = (jobSeeker: JobSeeker) => {
    const {email_id, first_name, last_name} = jobSeeker?.job_seekers_id || {}; 
    const jobseeker_name = `${ first_name} ${last_name}`; 
    const jobseeker_email = email_id; 

    setSelectedJobSeeker({...jobSeeker, jobseeker_name, jobseeker_email});
    setOpenModal(true);
  };

  // Handle modal close
  const handleCloseModal = () => {
    setOpenModal(false);
    setSelectedJobSeeker(null);
    setCopiedId(null);
  };

  // Format date
  const formatDate = (date: string | undefined) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit', // optional
      hour12: true,      // set false for 24-hour format
    });
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography
          variant="h4"
          sx={{
            mb: 4,
            fontWeight: 700,
            color: theme.palette.primary.main,
            position: 'relative',
            display: 'inline-block',
            '&:after': {
              content: '""',
              position: 'absolute',
              bottom: -8,
              left: 0,
              width: 40,
              height: 4,
              backgroundColor: theme.palette.secondary.main,
              borderRadius: 2,
            },
          }}
        >
          Payment History
        </Typography>
      </Box>

      {/* Search Filter */}
      <Paper
        elevation={2}
        sx={{
          p: 2,
          mb: 3,
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          alignItems: { xs: 'stretch', md: 'center' },
          gap: 2,
        }}
      >
        <TextField
          label="Search Payment History"
          variant="outlined"
          size="small"
          fullWidth
          value={searchQuery}
          onChange={handleSearchChange}
          placeholder="Search by Candidate name, amount"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          sx={{ flexGrow: 1 }}
        />
        <Button variant="outlined" startIcon={<RefreshIcon />} onClick={resetFilters}>
          Reset
        </Button>
      </Paper>

      {/* Job Seekers Table */}
      <Paper elevation={3}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Invoice Number</TableCell>
                <TableCell>Job Seeker Name</TableCell>
                <TableCell>Amount</TableCell>
                <TableCell>Payment Status</TableCell>
                <TableCell>Payment Date</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 3 }}>
                    <CircularProgress size={40} />
                  </TableCell>
                </TableRow>
              ) : error ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 3, color: 'error.main' }}>
                    {error}
                  </TableCell>
                </TableRow>
              ) : jobSeekers.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 3 }}>
                    {searchQuery ? 'No data found matching your search' : 'No data found'}
                  </TableCell>
                </TableRow>
              ) : (
                jobSeekers.map((jobSeeker) => {
                  const {email_id, first_name, last_name} = jobSeeker?.job_seekers_id || {};
                  const jobseeker_name = `${ first_name} ${last_name}`;
                  // const jobseeker_email = email_id;
                  return (
                  <TableRow key={jobSeeker._id} hover>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography sx={{ fontWeight: 500 }}>
                          {jobSeeker.invoice_number
                            ? `${jobSeeker.invoice_number}`
                            : 'N/A'}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography sx={{ fontWeight: 500 }}>
                          {jobseeker_name? jobseeker_name: 'N/A'}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography sx={{ fontWeight: 500 }}>
                        ${jobSeeker.amount}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={jobSeeker.payment_status}
                        color={jobSeeker.payment_status === 'succeeded' ? 'success' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      {formatDate(jobSeeker?.createdAt)}
                    </TableCell>
                    <TableCell align="center">
                      <IconButton
                        color="primary"
                        size="small"
                        onClick={() => handleViewDetails(jobSeeker)}
                        sx={{
                          '&:hover': {
                            backgroundColor: alpha(theme.palette.primary.main, 0.1),
                          },
                        }}
                      >
                        <VisibilityIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                )})
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={totalRows}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>

      {/* Details Modal */}
      <Dialog
        open={openModal}
        onClose={handleCloseModal}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            maxHeight: '90vh',
          },
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            pb: 2,
            borderBottom: `1px solid ${theme.palette.divider}`,
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Payment Details
          </Typography>
          <IconButton onClick={handleCloseModal} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ mt: 2 }}>
          {selectedJobSeeker && (
            <Box>
              <Grid container spacing={3}>
                {/* Job Seeker Information */}
                <Grid item xs={12}>
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 2,
                      backgroundColor: alpha(theme.palette.primary.main, 0.02),
                    }}
                  >
                    <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600, color: theme.palette.primary.main }}>
                      Job Seeker Information
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                          <PersonIcon fontSize="small" color="action" />
                          <Typography variant="body2" color="text.secondary">Name:</Typography>
                        </Box>
                        <Typography variant="body1" sx={{ fontWeight: 500, ml: 4 }}>
                          {selectedJobSeeker.jobseeker_name || 'N/A'}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                          <EmailIcon fontSize="small" color="action" />
                          <Typography variant="body2" color="text.secondary">Email:</Typography>
                        </Box>
                        <Typography variant="body1" sx={{ fontWeight: 500, ml: 4 }}>
                          {selectedJobSeeker.jobseeker_email || 'N/A'}
                        </Typography>
                      </Grid>
                    </Grid>
                  </Paper>
                </Grid>

                {/* Payment ID */}
                <Grid item xs={12}>
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 2,
                      backgroundColor: alpha(theme.palette.grey[500], 0.02),
                    }}
                  >
                    <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
                      Payment ID
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1.5, backgroundColor: alpha(theme.palette.grey[500], 0.05), borderRadius: 1 }}>
                      <CodeIcon fontSize="small" color="action" />
                      <Typography
                        variant="body2"
                        sx={{
                          fontFamily: 'monospace',
                          wordBreak: 'break-all',
                          flexGrow: 1,
                        }}
                      >
                        {selectedJobSeeker?.payment_intent_id || 'N/A'}
                      </Typography>
                      <IconButton
                        size="small"
                        onClick={() => handleCopyPaymentId(selectedJobSeeker?.payment_intent_id || '')}
                        sx={{
                          '&:hover': {
                            backgroundColor: alpha(theme.palette.primary.main, 0.1),
                          },
                        }}
                        title="Copy Payment ID"
                      >
                        <ContentCopyIcon
                          fontSize="small"
                          sx={{
                            color: copiedId === selectedJobSeeker?.payment_intent_id
                              ? theme.palette.success.main
                              : theme.palette.action.active,
                          }}
                        />
                      </IconButton>
                    </Box>
                    {copiedId === selectedJobSeeker?.payment_intent_id && (
                      <Typography
                        variant="caption"
                        sx={{
                          display: 'block',
                          mt: 1,
                          color: theme.palette.success.main,
                          textAlign: 'center',
                        }}
                      >
                        Payment ID copied to clipboard!
                      </Typography>
                    )}
                  </Paper>
                </Grid>

                {/* Payment Information */}
                <Grid item xs={12}>
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 2,
                      backgroundColor: alpha(theme.palette.success.main, 0.02),
                    }}
                  >
                    <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600, color: theme.palette.success.main }}>
                      Payment Information
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <Box sx={{ mb: 2 }}>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                            Invoice Number:
                          </Typography>
                          <Typography variant="body1" sx={{ fontWeight: 500 }}>
                            {selectedJobSeeker.invoice_number || 'N/A'}
                          </Typography>
                        </Box>
                        <Box sx={{ mb: 2 }}>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                            Amount:
                          </Typography>
                          <Typography variant="h6" sx={{ fontWeight: 600, color: theme.palette.success.main }}>
                            ${selectedJobSeeker.amount}
                          </Typography>
                        </Box>
                        <Box>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                            Payment Status:
                          </Typography>
                          <Chip
                            label={selectedJobSeeker.payment_status}
                            color={selectedJobSeeker.payment_status === 'succeeded' ? 'success' : 'default'}
                            size="small"
                          />
                        </Box>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Box sx={{ mb: 2 }}>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                            Card Number:
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <CreditCardIcon fontSize="small" />
                            <Typography variant="body1" sx={{ fontWeight: 500 }}>
                              **** **** **** {selectedJobSeeker.card_number}
                            </Typography>
                          </Box>
                        </Box>
                        <Box>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                            Payment Date:
                          </Typography>
                          <Typography variant="body1" sx={{ fontWeight: 500 }}>
                            {formatDate(selectedJobSeeker?.createdAt)}
                          </Typography>
                        </Box>
                      </Grid>
                    </Grid>
                  </Paper>
                </Grid>

              </Grid>
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2, borderTop: `1px solid ${theme.palette.divider}` }}>
          <Button onClick={handleCloseModal} variant="contained">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PaymentHistory;



// import React from "react";

// const PaymentHistory = () => {
//     return (
//         <>
//             PaymentHistory
//         </>
//     )
// }

// export default PaymentHistory;